import { LayerItem, useLayers } from '@ais-ui/react-openlayers';
import React, { useCallback, useEffect, useState } from 'react';
import { useLocalization } from '@ais-ui/locale-management';
import {
  Button,
  Icons,
  Label,
  LineColorPickerWithNames,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Slider,
  Switch,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  useToast,
} from '@ais-ui/ui-core';
import { useShapesEdit, useShapesInfo } from '@ais-ui/api';
import { cn, isPresent } from '@ais-ui/utils';
import { shapeCallbacks } from '../../utils/mutateCallbacks';
import {
  PiEye,
  PiPalette,
  PiPaletteThin,
  PiPolygon,
  PiTag,
} from 'react-icons/pi';
import { baseColours } from '@ais-ui/basecolours';
import { round } from 'ol/math';
import { Ruler } from 'lucide-react';

interface AppearanceControlProps {
  layer: LayerItem;
  canToggleLabels: boolean;
  canChangeColor: boolean;
  canChangeTransparency: boolean;
  canChangeSize: boolean;
  className?: string;
}

export const AppearanceControl = React.forwardRef<
  HTMLButtonElement,
  AppearanceControlProps
>(
  (
    {
      layer,
      canToggleLabels,
      canChangeColor,
      canChangeTransparency,
      canChangeSize,
      className,
    },
    ref
  ) => {
    const { toggleLabels, changeColor, changeTransparency } = useLayers();
    const { messages } = useLocalization();
    const { toast } = useToast();
    const [isSubmitting, setIsSubmitting] = useState(false);

    const { data: shape } = useShapesInfo(layer.id);

    const { mutate } = useShapesEdit();

    const [labelsAreActive, setLabelsAreActive] = useState(
      layer?.properties?.showLabel
    );
    const [color, setColor] = useState(
      layer?.properties?.color ? layer.properties.color : 'blue'
    );
    const [transparency, setTransparency] = useState(
      isPresent(layer?.properties?.transparency)
        ? layer.properties.transparency
        : 0.4
    );

    const onSaveShapeProperties = useCallback(() => {
      if (!shape) {
        return;
      }
      setIsSubmitting(true);

      const callbacks = shapeCallbacks(
        toast,
        messages,
        'edit',
        setIsSubmitting
      );

      const extendedOnSuccess = () => {
        callbacks.onSuccess();
      };

      const extendedCallbacks = {
        ...callbacks,
        onSuccess: extendedOnSuccess,
      };

      const updatedShape = {
        ...shape,
        properties: {
          ...shape.properties,
          color: color,
          transparency: transparency,
          showLabel: labelsAreActive,
        },
      };

      mutate({ id: layer.id, json: updatedShape }, extendedCallbacks);
    }, [
      shape,
      color,
      transparency,
      labelsAreActive,
      mutate,
      layer.id,
      toast,
      messages,
      setIsSubmitting,
    ]);

    useEffect(() => {
      if (layer?.properties?.showLabel) {
        setLabelsAreActive(true);
      } else {
        setLabelsAreActive(false);
      }

      setColor(layer?.properties?.color || 'blue');
      setTransparency(
        isPresent(layer?.properties?.transparency)
          ? layer.properties.transparency
          : 0.4
      );
    }, [layer]);

    const [size, setSize] = useState(layer?.properties?.size || 50);

    useEffect(() => {
      if (layer?.properties) {
        setLabelsAreActive(!!layer.properties.showLabel);
        setColor(layer.properties.color || 'blue');
        setTransparency(
          isPresent(layer?.properties?.transparency)
            ? layer.properties.transparency
            : 0.4
        );
        setSize(layer.properties.size || 50);
      }
    }, [layer]);

    return (
      <Tooltip>
        <Popover>
          <PopoverTrigger asChild>
            <TooltipTrigger asChild>
              <Button
                ref={ref}
                size="icon"
                variant="ghost"
                className={cn('h-9 w-9 flex-shrink-0 p-0 m-0', className)}
              >
                <PiPaletteThin className="h-5 w-5" />
              </Button>
            </TooltipTrigger>
          </PopoverTrigger>
          <TooltipContent>Change layer appearances</TooltipContent>
          <PopoverContent className="w-[340px]">
            <div className="space-y-6">
              {canChangeColor && (
                <div className="space-y-2">
                  <div className="flex flex-col justify-between">
                    <div className="flex items-center space-x-2">
                      <PiPalette className="h-4 w-4 text-gray-500" />
                      <Label className="text-sm">Color</Label>
                    </div>
                    <LineColorPickerWithNames
                      background={color}
                      setBackground={(color) => {
                        changeColor(layer, color as keyof typeof baseColours);
                      }}
                      className="p-0 pt-4 pb-2"
                    />
                  </div>
                </div>
              )}
              {canToggleLabels && (
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <PiTag className="h-4 w-4 text-gray-500" />
                    <Label htmlFor="labels-toggle">Labels</Label>
                  </div>
                  <Switch
                    id="labels-toggle"
                    checked={labelsAreActive}
                    onCheckedChange={() => toggleLabels(layer)}
                  />
                </div>
              )}
              {canChangeTransparency && (
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <PiEye className="h-4 w-4 text-gray-500" />
                    <Label htmlFor="transparency-slider" className="text-sm">
                      Transparency
                    </Label>
                    <span className="text-xs text-gray-500 ml-auto">
                      {round(transparency * 100, 0)}%
                    </span>
                  </div>
                  <Slider
                    id="transparency-slider"
                    min={0}
                    max={95}
                    step={1}
                    value={[transparency * 100]}
                    onValueChange={(val) => {
                      changeTransparency(layer, val[0] / 100);
                      setTransparency(val[0] / 100);
                    }}
                    className="w-full"
                  />
                </div>
              )}

              {canChangeSize && (
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <Ruler className="h-4 w-4 text-gray-500" />
                    <Label htmlFor="size-slider" className="text-sm">
                      Size
                    </Label>
                    <span className="text-xs text-gray-500 ml-auto">
                      {size}
                    </span>
                  </div>
                  <Slider
                    id="size-slider"
                    min={0}
                    max={100}
                    step={1}
                    value={[size]}
                    onValueChange={(val) => setSize(val[0])}
                    className="w-full"
                  />
                </div>
              )}
            </div>
            <div className="flex justify-end mt-6 pt-4 border-t">
              <Button
                size="sm"
                className="px-4 flex items-center gap-2"
                onClick={onSaveShapeProperties}
                disabled={isSubmitting || !shape}
              >
                {isSubmitting ? (
                  <>
                    <Icons.spinner className="h-4 w-4 animate-spin" />
                    <span>Saving...</span>
                  </>
                ) : (
                  <>
                    <PiPolygon className="h-4 w-4" />
                    <span>Save to Shape</span>
                  </>
                )}
              </Button>
            </div>
          </PopoverContent>
        </Popover>
      </Tooltip>
    );
  }
);
